import React, { useState, useEffect } from 'react';
import { Table, message } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import TableHeader from "../../components/Common/TableHeader";
import Breadcrumb from '../../components/Common/Breadcrumb';
import DepartmentModal from "../../components/Department/DepartmentModal";
import { addDepartment, fetchAllDepartments, updateDepartment } from "../../services/departmentService";
import { onShowSizeChange, itemRender } from '../../components/Pagination';
import { encrypt } from '../../utils/encryption';
import LoadingSpinner from '../../components/Common/LoadingSpinner'; // 👈 Add this line
import { jwtDecode } from "jwt-decode";
import { fetchUserById } from "../../services/userService";


const ListDepartment = () => {
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loggedInUserData, setLoggedInUserData] = useState(null);
  

  const [formData, setFormData] = useState({
    department: '',
    shortCode: '',
    type: '',
    intake: '',
    status: ''
  });

  const breadcrumbItems = [
    { label: "Department" },
    { label: "List Department" },
  ];

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const data = await fetchAllDepartments();
      const formatted = data.map((dept) => ({
        id: dept.department_id,
        Department: dept.department_name,
        ShorCode: dept.department_shortcode,
        type: dept.department_type,
        Intake: dept.department_intake,
        status: dept.department_status?.toString() || '0',
        "HOD Name": "N/A",
      }));
      setDepartments(formatted);
    } catch (err) {
      message.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
      const fetchAndSetUser = async () => {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const decoded = jwtDecode(token);
            const user = await fetchUserById(decoded.id);
            setLoggedInUserData(user);
          } catch (error) {
            console.error("Error decoding token or fetching user:", error);
            localStorage.removeItem("token");
            setLoggedInUserData(null);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      };
  
      fetchAndSetUser();
    }, []);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showAddModal = () => {
    setIsAddModalVisible(true);
    setFormData({
      department: '',
      shortCode: '',
      type: '',
      intake: '',
      status: ''
    });
  };

  const showEditModal = (record) => {
    setCurrentRecord(record);
    setFormData({
      department: record.Department,
      shortCode: record.ShorCode,
      type: record.type,
      intake: record.Intake,
      status: record.status?.toString() || '0'
    });
    setIsEditModalVisible(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { department, shortCode, type, intake, status } = formData;

      if (!department || !shortCode || !type || !intake || !status) {
        throw new Error("All fields are required");
      }

      const payload = new FormData();
      payload.append("name", department);
      payload.append("shortcode", shortCode);
      payload.append("intake", intake);
      payload.append("type", type);
      payload.append("status", status);
      payload.append("addedbyemail", loggedInUserData.user_email)

      if (isAddModalVisible) {
        await addDepartment({
          name: department,
          shortcode: shortCode,
          intake: intake,
          type: type,
          status: status,
          addedbyemail : loggedInUserData.user_email
        });
        message.success("Department Added Successfully.");
      }

      if (isEditModalVisible && currentRecord?.id) {
        payload.append("id", currentRecord.id);
        await updateDepartment(currentRecord.id, payload);
        message.success("Department Updated Successfully.");
      }

      await fetchDepartments();
      setTimeout(() => handleCloseModal(), 1000);
    } catch (err) {
      message.error(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsAddModalVisible(false);
    setIsEditModalVisible(false);
    setCurrentRecord(null);
  };

  const columns = [
    { title: "Department", dataIndex: "Department" },
    {
      title: "Short Code",
      dataIndex: "ShorCode",
      sorter: (a, b) => a.ShorCode.localeCompare(b.ShorCode),
    },
    {
      title: "Type",
      dataIndex: "type",
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    {
      title: "HOD Name",
      dataIndex: "HOD Name"
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <span className={`badge ${status === '1' ? 'bg-success' : 'bg-secondary'}`}>
          {status === '1' ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      title: "Actions",
      render: (_, record) => {
        const encryptedId = encrypt(record.id);
        return (
          <div className="d-flex gap-2">
            <Link to={`/view-department/${encodeURIComponent(encryptedId)}`}>
              <button className="btn btn-primary float-end">View Dpt.</button>
            </Link>
            <button
              className="btn btn-secondaryi float-end"
              onClick={() => showEditModal(record)}
            >
              Edit
            </button>
          </div>    
        );
      },
    }
  ];

  return (
    <div>
      <Helmet><title>Department List | CRM Jnec</title></Helmet>
      <Header />
      <Sidebar activeClassName='list-department' />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12"><Breadcrumb items={breadcrumbItems} /></div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message="Loading departments..." />
          ) : (
            <div className="row">
              <div className="col-sm-12">
                <div className="card card-table show-entire">
                  <div className="card-body">
                    <div className="page-table-header mb-2">
                      <div className="row align-items-center">
                        <div className="col">
                          <TableHeader title="Department List" addlink="" />
                          <button className="btn btn-primary float-end" onClick={showAddModal}>
                            Add Department
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <Table
                        pagination={{
                          total: departments.length,
                          showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                          onShowSizeChange: onShowSizeChange,
                          itemRender: itemRender,
                        }}
                        columns={columns}
                        dataSource={departments}
                        rowKey={(record) => record.id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DepartmentModal
        isVisible={isAddModalVisible || isEditModalVisible}
        isAdd={isAddModalVisible}
        formData={formData}
        onChange={handleInputChange}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default ListDepartment;
